import { useQuery } from '@tanstack/react-query'
import type { ProductRole } from '@dcp/shared'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'
import { useOrganization } from '@/contexts/OrganizationContext'
import { type OrganizationInvitation, USER_INVITE_TYPE } from './useOrganizationInvitations'

/**
 * Team member with profile info
 */
export interface TeamMember {
  id: string
  email: string
  full_name: string | null
  role: string
  product_role: ProductRole | null
  created_at: string
}

/**
 * Query keys for team management
 */
const TEAM_KEYS = {
  members: (orgId?: string) => ['team-members', orgId] as const,
  invitations: (orgId?: string) => ['team-invitations', orgId] as const,
}

/**
 * Hook to fetch organization members
 * Returns all profiles belonging to the current organization
 */
export function useOrgMembers() {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: TEAM_KEYS.members(orgId),
    queryFn: async () => {
      if (!orgId) return []

      const supabase = getSupabaseClient()

      // Get members who belong to this organization (org_owner, tech_team, user, viewer)
      const { data: orgMembers, error: orgError } = await supabase
        .from('profiles')
        .select('id, email, full_name, role, product_role, created_at')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: true })

      if (orgError) {
        if (import.meta.env.DEV) {
          console.error('Failed to fetch org members:', orgError)
        }
        throw new Error('Failed to fetch team members')
      }

      // Also get admins who have access to this organization via user_organization_access
      const { data: accessMembers, error: accessError } = await supabase
        .from('user_organization_access')
        .select(`
          user_id,
          profiles!inner(id, email, full_name, role, product_role, created_at)
        `)
        .eq('organization_id', orgId)

      if (accessError) {
        if (import.meta.env.DEV) {
          console.error('Failed to fetch access members:', accessError)
        }
        // Don't throw - org members are more important
      }

      // Combine and dedupe
      const allMembers: TeamMember[] = [...(orgMembers || [])]

      if (accessMembers) {
        for (const access of accessMembers) {
          const profile = access.profiles as unknown as TeamMember
          if (profile && !allMembers.find(m => m.id === profile.id)) {
            allMembers.push(profile)
          }
        }
      }

      return allMembers
    },
    enabled: !!orgId,
  })
}

/**
 * Hook to fetch pending invitations for the current organization
 * Returns invitations with invite_type='user' and target_organization_id matching current org
 */
export function useOrgInvitations() {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: TEAM_KEYS.invitations(orgId),
    queryFn: async () => {
      if (!orgId) return []

      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required')
      }

      // Fetch invitations via edge function (RLS will filter appropriately)
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'list',
            status: 'open',
          }),
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to fetch invitations')
      }

      const result = await response.json()

      // Filter to only user-type invitations for this organization
      return (result.data || []).filter(
        (inv: OrganizationInvitation) =>
          inv.invite_type === USER_INVITE_TYPE && inv.target_organization_id === orgId
      )
    },
    enabled: !!orgId,
  })
}
